/** Categories are used to group multiple services together. A service must be associated with a category in order to be exposed in the Wix Bookings UI. */
export interface Category {
  /**
   * Category ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Category name.
   * @maxLength 500
   */
  name?: string | null;
  /**
   * Category status.
   *
   * Default: `CREATED`
   * @readonly
   */
  status?: StatusWithLiterals;
  /** Sort order of the category in the live site and dashboard. */
  sortOrder?: number | null;
}

export enum Status {
  /** The category was created. */
  CREATED = 'CREATED',
  /** The category was deleted. */
  DELETED = 'DELETED',
}

/** @enumType */
export type StatusWithLiterals = Status | 'CREATED' | 'DELETED';

export interface ListCategoryRequest {
  /**
   * IDs of the categories to retrieve.
   *
   * Default: All categories are retrieved.
   * @format GUID
   */
  categoryIds?: string[];
  /**
   * Whether to include deleted categories in the response.
   *
   * Default: `false`
   */
  includeDeleted?: boolean | null;
}

export interface ListCategoryResponse {
  /** Retrieved categories. */
  categories?: Category[];
}

/** An event sent every time a category entity is changed. */
export interface CategoryNotification {
  category?: Category;
  event?: EventWithLiterals;
}

export enum Event {
  /** Category was updated. */
  Updated = 'Updated',
  /** Category was deleted. */
  Deleted = 'Deleted',
  /** Category was created. */
  Created = 'Created',
}

/** @enumType */
export type EventWithLiterals = Event | 'Updated' | 'Deleted' | 'Created';

export interface CreateCategoryRequest {
  /** Category to create. */
  category: Category;
}

export interface CreateCategoryResponse {
  /** Created category. */
  category?: Category;
}

export interface BatchCreateCategoryRequest {
  /** Categories to create. */
  categories?: Category[];
}

export interface BatchCreateCategoryResponse {
  /** Created categories. */
  categories?: Category[];
}

export interface UpdateCategoryRequest {
  /** Category to update. */
  category: Category;
}

export interface UpdateCategoryResponse {
  /** Updated category. */
  category?: Category;
}

export interface DeleteCategoryRequest {
  /**
   * ID of the category to delete.
   * @format GUID
   */
  id: string | null;
  /**
   * Whether to delete all the services associated with the category.
   *
   * Default: `false`
   */
  deleteServices?: boolean | null;
}

export interface DeleteCategoryResponse {
  /**
   * ID of the deleted category.
   * @format GUID
   */
  id?: string | null;
}

export interface BatchDeleteCategoryRequest {
  /**
   * IDs of the categories to delete.
   * @format GUID
   */
  ids?: string[] | null;
}

export interface BatchDeleteCategoryResponse {}

export interface BatchUpdateCategoryRequest {
  /** List of categories to be updated. */
  categories?: Category[];
  /** Field mask of fields to update. */
  fieldMask?: string[];
}

export interface BatchUpdateCategoryResponse {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type UpdateApplicationErrors = {
  code?: 'NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
